import React, { forwardRef, useImperativeHandle, useState } from 'react';
import '../componentStyle/visualEffectsPanel.css';
import { X } from 'lucide-react';

const VisualEffectsPanel = forwardRef(({
  CbFromParticipant,
  options,
  selectedEffectId,
  onSelectEffect,
  isApplying,
  pendingEffectId,
  canApply,
  isDarkTheme
}, ref) => {
  const [isOpen, setIsOpen] = useState(false);

  useImperativeHandle(ref, () => ({
    open: () => {
      CbFromParticipant({ isOpen: true, from: 'visualEffects' });
      setIsOpen(true);
    },
    close: () => {
      CbFromParticipant({ isOpen: false, from: 'visualEffects' });
      setIsOpen(false);
    }
  }), [CbFromParticipant]);

  const handleClose = () => {
    CbFromParticipant({ isOpen: false, from: 'visualEffects' });
    setIsOpen(false);
  };

  const renderPreviewStyle = (option) => {
    const baseStyle = {
      backgroundColor: '#131a2a',
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat'
    };

    if (option.thumbnailStyle) {
      return { ...baseStyle, ...option.thumbnailStyle };
    }

    if (option.thumbnailUrl) {
      return { ...baseStyle, backgroundImage: `url(${option.thumbnailUrl})` };
    }

    if (option.imageUrl) {
      return { ...baseStyle, backgroundImage: `url(${option.imageUrl})` };
    }

    return baseStyle;
  };

  if (!isOpen) {
    return null;
  }

  const effectGroups = Array.isArray(options) ? options : [];

  return (
    <div className={`participant-panel visual-effects-panel ${isDarkTheme ? 'dark' : 'light'}`}>
      <div className='panel-header'>
        <div className='header-style'>Visual Effects</div>
        <X className='panel-close-icon' onClick={handleClose} />
      </div>
      <div className='visual-effects-body'>
        {!canApply && (
          <div className='visual-effects-alert'>
            Turn on your camera to enable visual effects.
          </div>
        )}

        {effectGroups.map((group) => (
          <section key={group.id} className='visual-effects-section'>
            <div className='visual-effects-section-header'>
              <span className='visual-effects-section-title'>{group.title}</span>
              {group.subtitle && (
                <span className='visual-effects-section-subtitle'>{group.subtitle}</span>
              )}
            </div>
            <div
              className={`visual-effects-grid${group.items?.some((item) => item.id === 'none') ? ' no-grid' : ''}`}
            >
              {(group.items || []).map((option) => {
                const isSelected = selectedEffectId === option.id;
                const isPending = pendingEffectId === option.id && canApply;
                const isDisabled = !canApply || (isApplying && !isSelected);
                const cardKind = option.category === 'effect' ? 'effect-card' : 'image-card';
                const sizeClass = option.size === 'compact' ? 'compact' : 'regular';

                const containerClass = option.id === 'none'
                  ? 'visual-effects-card reset-card'
                  : `visual-effects-card ${cardKind} ${sizeClass}`;

                return (
                  <button
                    key={option.id}
                    type='button'
                    className={`${containerClass} ${isSelected ? 'selected' : ''} ${isDisabled ? 'disabled' : ''}`}
                    onClick={() => canApply && onSelectEffect(option)}
                    disabled={isDisabled}
                    title={option.label}
                  >
                    <div className='visual-effects-thumb' style={renderPreviewStyle(option)}>
                      {option.badge && (
                        <span className='visual-effects-chip'>{option.badge}</span>
                      )}
                      {isPending && <div className='visual-effects-spinner' />}
                      {isSelected && !isPending && (
                        <span className='visual-effects-badge'>Active</span>
                      )}
                    </div>
                    {!option.hideLabel && (
                      <div className='visual-effects-meta'>
                        <span className='visual-effects-title'>{option.label}</span>
                        {option.description && (
                          <span className='visual-effects-description'>{option.description}</span>
                        )}
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
});

export default VisualEffectsPanel;

